import React, { createContext, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { createLookupHash } from '../../services/hash-service';
import Global from '../../store/global-selectors';
import { bulkLoadProductData } from '../../store/products/products.actions';
import { convertWeight } from '../../services/unit-services/weights/weight-convert-service';

import { ShouldShowBundleOnPackingSlipName, BundlePackingSlipDisplayMode } from './constants/product-options';
import { GuidService } from 'ui-components/services/guid-service';
import { bundlesSchema } from './bundles-schema';

const BundleContext = createContext();

const validateBundle = (component) => {
  try {
    bundlesSchema.validateSync(component, { abortEarly: false });
  } catch (e) {
    return e;
  }
};

export const mapLegacyOrEnumValue = (raw) => {
  if (raw == null || raw === '') {
    return BundlePackingSlipDisplayMode.ParentOnly;
  }
  if (
    raw === BundlePackingSlipDisplayMode.ParentOnly ||
    raw === BundlePackingSlipDisplayMode.ChildrenOnly ||
    raw === BundlePackingSlipDisplayMode.ParentAndChildren
  ) {
    return raw;
  }
  if (raw === 'true' || raw === true) {
    return BundlePackingSlipDisplayMode.ParentOnly;
  }
  if (raw === 'false' || raw === false) {
    return BundlePackingSlipDisplayMode.ChildrenOnly;
  }
  return BundlePackingSlipDisplayMode.ChildrenOnly;
};

const BundleContextProvider = ({ children, isBundle: isBundleProp = false, bundleComponents: bundleComponentsProp, productOptions }) => {
  const dispatch = useDispatch();
  const bundleComponents = useMemo(
    () =>
      bundleComponentsProp?.map((c) => ({
        readonly: true,
        id: GuidService.newGuid(),
        ...c,
      })) ?? [],
    [bundleComponentsProp],
  );
  const componentEntities = useSelector((state) =>
    createLookupHash(
      Global.selectEntitiesProductsByIds(state, {
        productIds: bundleComponents.map((b) => b.productId),
      }),
      'productId',
    ),
  );

  const [isBundle, setIsBundle] = useState(isBundleProp);
  const [components, setComponents] = useState(() =>
    bundleComponents.map((bc) => {
      const entity = componentEntities[bc.productId];
      return {
        ...bc,
        sku: entity.sku,
        name: entity.name,
      };
    }),
  );

  const componentsWeight = useMemo(() => {
    const weights = components
      .filter((c) => c.productId)
      .map((comp) => {
        const { weight, weightUnitOfMeasureId } = componentEntities[comp.productId] ?? {};

        return {
          value: (weight ?? 0) * comp.quantity,
          unit: weightUnitOfMeasureId,
        };
      });

    if (weights.length === 0) {
      return null;
    }

    return weights.reduce((acc, weight) => {
      return {
        value: acc.value + (convertWeight(weight, acc.unit).value || 0),
        unit: acc.unit,
      };
    });
  }, [components, componentEntities]);

  const shouldShowBundleOnPackingSlipValue = productOptions?.find((po) => po.name === ShouldShowBundleOnPackingSlipName)?.value;
  const isValueOriginallySet = shouldShowBundleOnPackingSlipValue != null && shouldShowBundleOnPackingSlipValue !== '';
  const [bundlePackingSlipDisplayMode, setBundlePackingSlipDisplayMode] = useState(
    mapLegacyOrEnumValue(shouldShowBundleOnPackingSlipValue),
  );
  const [hasExplicitBundlePackingSlipDisplayMode, setHasExplicitBundlePackingSlipDisplayMode] = useState(isValueOriginallySet);

  const handleSetBundlePackingSlipDisplayMode = useCallback((mode) => {
    setBundlePackingSlipDisplayMode(mode);
    setHasExplicitBundlePackingSlipDisplayMode(true);
  }, []);

  const addBundleComponent = useCallback(
    () =>
      setComponents((prevState) => {
        const component = {
          id: GuidService.newGuid(),
          sku: '',
          name: '',
          quantity: 1,
        };
        return [...prevState, component];
      }),
    [],
  );

  const removeBundleComponent = useCallback((id) => {
    setComponents((prevState) => {
      return prevState.filter((c) => c.id !== id);
    });
  }, []);

  const getApiBundleComponents = useCallback(() => {
    if (!components) {
      return [];
    }
    return components.map((bc) => ({
      productId: bc.productId,
      quantity: bc.quantity,
    }));
  }, [components]);

  const editBundleComponent = useCallback(
    (id, newComponent) => {
      setComponents((prevState) => {
        const newState = prevState.map((component) => {
          if (component.id === id) {
            const comp = {
              ...component,
              ...newComponent,
            };
            comp.error = validateBundle(comp);
            return comp;
          }
          return component;
        });

        dispatch(bulkLoadProductData(newState.filter((s) => s.productId != null).map((s) => s.productId)));
        return newState;
      });
    },
    [dispatch],
  );

  const isBundleValid = useCallback(() => {
    let isValid = true;
    setComponents((prevComponents) => {
      return prevComponents.map((comp) => {
        const error = validateBundle(comp);

        isValid = isValid && error == null;
        return {
          ...comp,
          error,

          shouldShowError: error != null,
        };
      });
    });

    return isValid;
  }, []);

  const value = useMemo(
    () => ({
      isBundle,
      setIsBundle,
      isBundleValid,
      bundleComponents: components,
      componentsWeight,
      addBundleComponent,
      editBundleComponent,
      removeBundleComponent,
      getApiBundleComponents,
      bundlePackingSlipDisplayMode,
      setBundlePackingSlipDisplayMode: handleSetBundlePackingSlipDisplayMode,
      hasExplicitBundlePackingSlipDisplayMode,
    }),
    [
      isBundle,
      setIsBundle,
      isBundleValid,
      components,
      componentsWeight,
      addBundleComponent,
      editBundleComponent,
      removeBundleComponent,
      getApiBundleComponents,
      bundlePackingSlipDisplayMode,
      handleSetBundlePackingSlipDisplayMode,
      hasExplicitBundlePackingSlipDisplayMode,
    ],
  );

  return <BundleContext.Provider value={value}>{children}</BundleContext.Provider>;
};

BundleContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  isBundle: PropTypes.bool,
  bundleComponents: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string,
      quantity: PropTypes.number,
    }),
  ),
  productOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  ),
};

export { BundleContext, BundleContextProvider };
